import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import communityService, { type MyCommunityItem } from '@/services/communityService';
import { CommunityListSkeleton } from '@/components/client/skeleton/ClientSkeletons';

const getInitials = (name: string): string => {
  if (!name) return 'KM';
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const ClientMyCommunityPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [myCommunities, setMyCommunities] = useState<MyCommunityItem[]>([]);
  const [discoverCommunities, setDiscoverCommunities] = useState<MyCommunityItem[]>([]);

  const [actionLoadingSlug, setActionLoadingSlug] = useState<string | null>(null);

  // Bottom Popup Confirmation state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    isClosing: boolean;
    type: 'join' | 'leave';
    slug: string;
    name: string;
  }>({
    isOpen: false,
    isClosing: false,
    type: 'join',
    slug: '',
    name: '',
  });

  const closeConfirmModal = () => {
    if (confirmModal.isClosing) return;
    setConfirmModal((prev) => ({ ...prev, isClosing: true }));
    setTimeout(() => {
      setConfirmModal((prev) => ({ ...prev, isOpen: false, isClosing: false }));
    }, 280);
  };

  const fetchCommunities = useCallback(async (query: string) => {
    try {
      setLoading(true);
      const [myRes, discRes] = await Promise.allSettled([
        communityService.getMyCommunities(),
        communityService.getDiscoverCommunities({
          search: query || undefined,
          limit: 20,
          page_my_community: true,
        }),
      ]);

      if (myRes.status === 'fulfilled' && myRes.value.success) {
        setMyCommunities(myRes.value.data || []);
      } else {
        setMyCommunities([]);
      }

      if (discRes.status === 'fulfilled' && discRes.value.success) {
        setDiscoverCommunities(discRes.value.data || []);
      } else {
        setDiscoverCommunities([]);
      }
    } catch (err) {
      console.error('Failed to load communities:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCommunities(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchCommunities]);

  const handleJoin = async (slug: string, name: string) => {
    try {
      setActionLoadingSlug(slug);
      const res = await communityService.joinCommunity(slug);
      if (res.success) {
        showToast.success(`Berhasil bergabung dengan ${name}!`);
        await fetchCommunities(searchQuery);
      } else {
        showToast.error(res.message || 'Gagal bergabung dengan komunitas');
      }
    } catch (err: any) {
      showToast.error(err?.response?.data?.message || 'Gagal bergabung dengan komunitas');
    } finally {
      setActionLoadingSlug(null);
    }
  };

  const handleLeave = async (slug: string, name: string) => {
    try {
      setActionLoadingSlug(slug);
      const res = await communityService.leaveCommunity(slug);
      if (res.success) {
        showToast.success(`Kamu telah meninggalkan ${name}`);
        await fetchCommunities(searchQuery);
      } else {
        showToast.error(res.message || 'Gagal meninggalkan komunitas');
      }
    } catch (err: any) {
      showToast.error(err?.response?.data?.message || 'Gagal meninggalkan komunitas');
    } finally {
      setActionLoadingSlug(null);
    }
  };

  const promptJoin = (slug: string, name: string) => {
    setConfirmModal({
      isOpen: true,
      isClosing: false,
      type: 'join',
      slug,
      name,
    });
  };

  const promptLeave = (slug: string, name: string) => {
    setConfirmModal({
      isOpen: true,
      isClosing: false,
      type: 'leave',
      slug,
      name,
    });
  };

  const handleConfirmAction = async () => {
    const { type, slug, name } = confirmModal;
    closeConfirmModal();
    if (type === 'join') {
      await handleJoin(slug, name);
    } else {
      await handleLeave(slug, name);
    }
  };

  // Filter myCommunities by searchQuery
  const filteredMyCommunities = myCommunities.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="bg-sporto-bg min-h-screen text-sporto-dark pb-8">
      <div className="max-w-2xl mx-auto px-4 pt-4 md:pt-6 space-y-5">
        {/* Search Bar */}
        <div className="relative flex items-center">
          <input
            type="text"
            placeholder="Cari komunitas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-12 pl-11 pr-4 rounded-2xl border border-[#E0E6ED] bg-white text-sm text-[#0E1726] font-medium placeholder-gray-400 focus:outline-none focus:border-[#FC480D] shadow-2xs transition-colors"
          />
          <div className="absolute left-4 text-gray-400 pointer-events-none">
            <Icon icon="ph:magnifying-glass-bold" className="w-5 h-5" />
          </div>
        </div>

        {/* Loading Skeleton */}
        {loading ? (
          <CommunityListSkeleton />
        ) : (
          <div className="space-y-5 animate-fade-in">
            {/* SECTION 1: GRUP KAMU */}
            <div className="space-y-2.5">
              <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider px-1">
                GRUP KAMU ({filteredMyCommunities.length})
              </h2>

              <div className="bg-white rounded-2xl p-2 sm:p-3 border border-[#E0E6ED] divide-y divide-gray-100 shadow-2xs">
                {filteredMyCommunities.length === 0 ? (
                  <div className="py-6 text-center text-xs text-gray-400 font-medium">
                    {searchQuery
                      ? 'Tidak ada komunitas yang cocok dalam grup kamu.'
                      : 'Kamu belum bergabung dalam komunitas manapun.'}
                  </div>
                ) : (
                  filteredMyCommunities.map((item) => {
                    const isProcessing = actionLoadingSlug === item.slug;
                    return (
                      <div
                        key={item.slug}
                        className={`py-3 px-2 flex items-center justify-between gap-3 rounded-xl transition-all duration-200 ${
                          isProcessing ? 'bg-gray-50 opacity-70 animate-pulse' : 'hover:bg-gray-50/50'
                        }`}
                      >
                        <div
                          onClick={() => navigate(`/community/${item.slug}`)}
                          className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer"
                        >
                          {item.logo ? (
                            <img
                              src={item.logo}
                              alt={item.name}
                              className="w-11 h-11 rounded-full object-cover shrink-0 border border-gray-100"
                            />
                          ) : (
                            <div className="w-11 h-11 rounded-full bg-orange-100 text-[#FC480D] font-bold text-sm flex items-center justify-center shrink-0 border border-orange-200">
                              {getInitials(item.name)}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm font-bold text-[#0E1726] truncate">
                              {item.name}
                            </h3>
                            <p className="text-xs text-gray-400 font-normal mt-0.5 truncate">
                              {item.totalMembers.toLocaleString('id-ID')} anggota
                            </p>
                          </div>
                        </div>

                        <button
                          type="button"
                          disabled={isProcessing}
                          onClick={() => promptLeave(item.slug, item.name)}
                          className="h-9 px-4 rounded-xl bg-gray-100 hover:bg-red-50 text-gray-500 hover:text-red-600 disabled:opacity-50 font-semibold text-xs transition-colors cursor-pointer shrink-0 border border-transparent flex items-center justify-center gap-1.5 min-w-[90px]"
                        >
                          {isProcessing ? (
                            <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin text-gray-400" />
                          ) : (
                            <span>Tinggalkan</span>
                          )}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* SECTION 2: TEMUKAN KOMUNITAS BARU */}
            <div className="space-y-2.5 pt-2">
              <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider px-1">
                TEMUKAN KOMUNITAS BARU
              </h2>

              <div className="bg-white rounded-2xl p-2 sm:p-3 border border-[#E0E6ED] divide-y divide-gray-100 shadow-2xs">
                {discoverCommunities.length === 0 ? (
                  <div className="py-6 text-center text-xs text-gray-400 font-medium">
                    Tidak ada komunitas baru yang ditemukan.
                  </div>
                ) : (
                  discoverCommunities.map((item) => {
                    const isProcessing = actionLoadingSlug === item.slug;
                    return (
                      <div
                        key={item.slug}
                        className={`py-3 px-2 flex items-center justify-between gap-3 rounded-xl transition-all duration-200 ${
                          isProcessing ? 'bg-gray-50 opacity-70 animate-pulse' : 'hover:bg-gray-50/50'
                        }`}
                      >
                        <div
                          onClick={() => navigate(`/community/${item.slug}`)}
                          className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer"
                        >
                          {item.logo ? (
                            <img
                              src={item.logo}
                              alt={item.name}
                              className="w-11 h-11 rounded-full object-cover shrink-0 border border-gray-100"
                            />
                          ) : (
                            <div className="w-11 h-11 rounded-full bg-slate-100 text-slate-600 font-bold text-sm flex items-center justify-center shrink-0 border border-slate-200">
                              {getInitials(item.name)}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm font-bold text-[#0E1726] truncate">
                              {item.name}
                            </h3>
                            <p className="text-xs text-gray-400 font-normal mt-0.5 truncate">
                              {item.totalMembers.toLocaleString('id-ID')} anggota
                            </p>
                          </div>
                        </div>

                        <button
                          type="button"
                          disabled={isProcessing}
                          onClick={() => promptJoin(item.slug, item.name)}
                          className="h-9 px-5 rounded-xl bg-[#FC480D] hover:bg-[#D83B08] disabled:opacity-50 text-white font-bold text-xs shadow-2xs transition-all active:scale-98 cursor-pointer shrink-0 flex items-center justify-center gap-1.5 min-w-[80px]"
                        >
                          {isProcessing ? (
                            <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin text-white" />
                          ) : (
                            <span>Gabung</span>
                          )}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Popup Confirmation Modal */}
      {confirmModal.isOpen && (
        <div
          className={`fixed inset-0 z-50 bg-[#0E1726]/60 backdrop-blur-xs flex items-end justify-center transition-opacity duration-300 ease-in-out ${
            confirmModal.isClosing ? 'opacity-0 pointer-events-none' : 'opacity-100 animate-in fade-in duration-200'
          }`}
          onClick={closeConfirmModal}
        >
          <div
            className={`w-full max-w-md bg-white rounded-t-3xl p-5 sm:p-6 flex flex-col gap-4 shadow-2xl transition-transform duration-300 ease-in-out ${
              confirmModal.isClosing
                ? 'translate-y-full'
                : 'translate-y-0 animate-in slide-in-from-bottom duration-300'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle bar */}
            <div className="w-12 h-1 rounded-full bg-gray-300 self-center -mt-1 mb-1" />

            {/* Icon & Title */}
            <div className="flex flex-col items-center text-center space-y-2 pt-1">
              <div
                className={`w-14 h-14 rounded-2xl flex items-center justify-center border shadow-2xs ${
                  confirmModal.type === 'join'
                    ? 'bg-orange-50 text-[#FC480D] border-orange-100'
                    : 'bg-rose-50 text-rose-600 border-rose-100'
                }`}
              >
                <Icon
                  icon={confirmModal.type === 'join' ? 'ph:users-three-bold' : 'ph:user-minus-bold'}
                  className="w-7 h-7"
                />
              </div>
              <h3 className="text-lg font-bold text-[#0E1726]">
                {confirmModal.type === 'join' ? 'Bergabung ke Komunitas?' : 'Tinggalkan Komunitas?'}
              </h3>
              <p className="text-xs text-gray-500 font-medium max-w-xs leading-relaxed">
                {confirmModal.type === 'join' ? (
                  <>Apakah Anda yakin ingin bergabung dengan komunitas <strong className="text-[#0E1726]">{confirmModal.name}</strong>?</>
                ) : (
                  <>Apakah Anda yakin ingin keluar dari komunitas <strong className="text-[#0E1726]">{confirmModal.name}</strong>?</>
                )}
              </p>
            </div>

            {/* Action Buttons Row */}
            <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={closeConfirmModal}
                className="flex-1 h-11 rounded-full border border-[#E0E6ED] bg-white text-[#0E1726] font-bold text-xs hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleConfirmAction}
                className={`flex-1 h-11 rounded-full font-bold text-xs text-white shadow-2xs transition-all active:scale-98 cursor-pointer ${
                  confirmModal.type === 'join'
                    ? 'bg-[#FC480D] hover:bg-[#D83B08]'
                    : 'bg-rose-600 hover:bg-rose-700'
                }`}
              >
                {confirmModal.type === 'join' ? 'Ya, Bergabung' : 'Ya, Tinggalkan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientMyCommunityPage;
